import {toast} from 'react-toastify';
import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import {
    buildAdminRequestParams,
    createPlayPresentationUrl,
    createShortPresentationUrl,
    isPresentationRunnable,
    openPlayerWindow,
} from '../../../utils/presentation';
import {copyToClipboard} from '../../../utils/copyToClipboard';
import {useCreateAutoSlug} from '../../../service/hooks/useSlug';
import {exportPresentationAction, selectPresentationToEditAction} from '../../../store/actions/presentation';
import {duplicatePresentation} from '../../../service/presentation';
import {IPresentation} from '../../../incase-domain/presentation';
import {exportingPresentationsSelector} from '../../../store/selectors/presentation.selectors';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {DropdownMenuCompactTileWithOnClick} from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompactTile';
import Button from '../../../incase-domain/components/button/Button';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';
import {Role} from '../../../incase-domain/user';
import {type TypedTranslation} from '../../../i18n';

export enum BUTTON_OPTIONS {
    CREATE_PRESENTATION_LINKS = 'CREATE_PRESENTATION_LINKS',
    START_PRESENTATION = 'START_PRESENTATION',
    PRESENTATION_REPORTS = 'PRESENTATION_REPORTS',
    EDIT_PRESENTATIONS = 'EDIT_PRESENTATIONS',
    COPY_PRESENTATION = 'COPY_PRESENTATION',
    MOVE_TO_FOLDER = 'MOVE_TO_FOLDER',
    DOWNLOAD_PRESENTATION = 'DOWNLOAD_PRESENTATION',
    DELETE_PRESENTATION = 'DELETE_PRESENTATION',
    READ_ONLY = 'READ_ONLY',
    DIVIDER = 'DIVIDER',
    CREATE_ROOM = 'CREATE_ROOM',
    SHARE_PRESENTATION = 'SHARE_PRESENTATION',
}

type ButtonType = {
    type: BUTTON_OPTIONS;
    icon: string;
    label: string;
    tooltip: string;
    onClick?: () => void;
    disabled?: boolean;
    isLoading?: boolean;
    dataCy?: string;
    color?: 'error';
};

interface PresentationListButtonsProp {
    presentation: IPresentation;
    setDeletePresentations: React.Dispatch<React.SetStateAction<boolean>>;
    setPresentationToDelete: React.Dispatch<React.SetStateAction<IPresentation[]>>;
    onCreatePresentationLinks?: (presentation: IPresentation) => void;
    setSelectedForRoomCreation?: React.Dispatch<React.SetStateAction<IPresentation | undefined>>;
    buttonOptions: BUTTON_OPTIONS[];
    updateSharedPresentation?: () => void;
    setSendPresentationModal?: React.Dispatch<React.SetStateAction<boolean>>;
    setSelected?: (presentations: IPresentation[]) => void;
    onMovePresentation?: (presentation: IPresentation) => void;
}

export default function PresentationListButtons(props: PresentationListButtonsProp): JSX.Element {
    const {
        presentation,
        setDeletePresentations,
        setPresentationToDelete,
        onCreatePresentationLinks,
        setSelectedForRoomCreation,
        buttonOptions,
        updateSharedPresentation,
        setSendPresentationModal,
        setSelected,
        onMovePresentation,
    } = props;

    const dispatch = useDispatch();
    const {t} = useTranslation();

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const exportingPresentations = useSelector(exportingPresentationsSelector);

    const loggedInUser = useSelector(loggedInUserSelector);
    const allowedRoles = useMemo(() => [Role.SUPERUSER, Role.ADMIN], []);

    const [duplicatingPres, setDuplicatingPres] = useState('');
    const {mutateAsync: createAutoSlug} = useCreateAutoSlug();

    const needsLinkOptions = !!presentation.requestParams?.length;

    const onCopyLink = async (): Promise<void> => {
        if (!needsLinkOptions) {
            try {
                const slugDoc = await createAutoSlug({presentationId: presentation._id});
                await copyToClipboard(createShortPresentationUrl(slugDoc.slug));
                toast.info(t('presEditPage.subNavBar.copiedToClipboard'));
            } catch (err) {
                toast.info(t('presEditPage.subNavBar.failedToCopy'));
            }
            return;
        }
        onCreatePresentationLinks && onCreatePresentationLinks(presentation);
    };

    const copyCreateLinkText = (): string => {
        return needsLinkOptions
            ? 'editorPage.createPresentationLinksModal.createLinks'
            : 'editorPage.createPresentationLinksModal.copyLink';
    };

    const getButton = useCallback(
        (buttonType: BUTTON_OPTIONS): ButtonType => {
            switch (buttonType) {
                case BUTTON_OPTIONS.COPY_PRESENTATION:
                    return {
                        type: BUTTON_OPTIONS.COPY_PRESENTATION,
                        icon: 'solar:copy-linear',
                        label: 'editorPage.duplicate',
                        tooltip: 'editorPage.duplicate',
                        onClick: () => {
                            setDuplicatingPres(presentation._id);
                            duplicatePresentation(presentation._id).then((data) => {
                                setDuplicatingPres('');
                                if (data.errors) {
                                    if (data.errors.statusCode === 400) toast.error(t(data.errors.message as TypedTranslation));
                                    else toast.error(t('toasts.errorOccurred'));
                                } else {
                                    navigate(`/presentations/${data._id}`);
                                    queryClient.invalidateQueries(['presentationList']);
                                }
                            });
                        },
                        disabled: !!duplicatingPres,
                        isLoading: duplicatingPres === presentation._id,
                    };
                case BUTTON_OPTIONS.DOWNLOAD_PRESENTATION:
                    return {
                        type: BUTTON_OPTIONS.DOWNLOAD_PRESENTATION,
                        icon: 'solar:download-minimalistic-linear',
                        label: 'editorPage.download',
                        tooltip: 'editorPage.download',
                        onClick: () => {
                            dispatch(exportPresentationAction(presentation._id));
                        },
                        disabled: exportingPresentations.includes(presentation._id),
                        isLoading: exportingPresentations.includes(presentation._id),
                    };
                case BUTTON_OPTIONS.MOVE_TO_FOLDER:
                    return {
                        type: BUTTON_OPTIONS.MOVE_TO_FOLDER,
                        icon: 'solar:folder-move-linear',
                        label: 'editorPage.folders.moveToFolder',
                        tooltip: 'editorPage.folders.moveToFolder',
                        onClick: () => {
                            onMovePresentation && onMovePresentation(presentation);
                        },
                        disabled: presentation.isReadOnly,
                        dataCy: 'move_to_folder',
                    };
                case BUTTON_OPTIONS.DELETE_PRESENTATION:
                    return {
                        type: BUTTON_OPTIONS.DELETE_PRESENTATION,
                        icon: 'solar:trash-bin-minimalistic-linear',
                        label: 'editorPage.delete',
                        tooltip: 'editorPage.delete',
                        onClick: () => {
                            setDeletePresentations(true);
                            setPresentationToDelete([presentation as IPresentation]);
                        },
                        disabled: false,
                        dataCy: 'delete_presentation',
                        color: 'error',
                    };
                case BUTTON_OPTIONS.CREATE_PRESENTATION_LINKS:
                    return {
                        type: BUTTON_OPTIONS.CREATE_PRESENTATION_LINKS,
                        icon: 'solar:link-linear',
                        label: copyCreateLinkText(),
                        tooltip: copyCreateLinkText(),
                        onClick: onCopyLink,
                        dataCy: 'share_presentation',
                        disabled: !isPresentationRunnable(presentation.protectionTypes),
                    };
                case BUTTON_OPTIONS.START_PRESENTATION:
                    return {
                        type: BUTTON_OPTIONS.START_PRESENTATION,
                        icon: 'solar:play-linear',
                        label: 'editorPage.start',
                        tooltip: isPresentationRunnable(presentation.protectionTypes)
                            ? 'editorPage.start'
                            : 'presEditPage.subNavBar.tooltips.disabledStartPresentation',
                        onClick: () =>
                            openPlayerWindow(async () => {
                                const slugDoc = await createAutoSlug({
                                    presentationId: presentation._id,
                                    params: buildAdminRequestParams(presentation),
                                });
                                return createShortPresentationUrl(slugDoc.slug);
                            }, createPlayPresentationUrl({presentation})),
                        disabled: !isPresentationRunnable(presentation.protectionTypes),
                    };
                case BUTTON_OPTIONS.PRESENTATION_REPORTS:
                    if (loggedInUser?.roles.some((role) => allowedRoles.includes(role))) {
                        return {
                            type: BUTTON_OPTIONS.PRESENTATION_REPORTS,
                            icon: 'solar:chart-linear',
                            label: 'editorPage.presentationReports',
                            tooltip: 'editorPage.presentationReports',
                            onClick: () => {
                                dispatch(selectPresentationToEditAction(presentation._id, navigate));
                                navigate(`/presentations/${presentation._id}/report`);
                            },
                            disabled: presentation.isReadOnly,
                        };
                    }
                    return {
                        type: buttonType,
                        icon: '',
                        label: '',
                        tooltip: '',
                    };
                case BUTTON_OPTIONS.EDIT_PRESENTATIONS:
                    return {
                        type: BUTTON_OPTIONS.EDIT_PRESENTATIONS,
                        icon: 'solar:pen-2-linear',
                        label: 'editorPage.edit',
                        tooltip: 'editorPage.edit',
                        onClick: () => {
                            dispatch(selectPresentationToEditAction(presentation._id, navigate));
                            navigate(`/presentations/${presentation._id}`);
                        },
                        disabled: presentation.isReadOnly,
                        dataCy: 'Edit_Presentation',
                    };
                case BUTTON_OPTIONS.READ_ONLY:
                    return {
                        type: BUTTON_OPTIONS.READ_ONLY,
                        icon: presentation.isReadOnly ? 'solar:pen-2-linear' : 'solar:pen-2-broken',
                        label: presentation.isReadOnly ? 'editorPage.readOnly' : 'editorPage.edit',
                        tooltip: presentation.isReadOnly ? 'editorPage.readOnly' : 'editorPage.edit',
                        onClick: updateSharedPresentation,
                    };
                case BUTTON_OPTIONS.CREATE_ROOM:
                    return {
                        type: BUTTON_OPTIONS.CREATE_ROOM,
                        icon: 'solar:play-circle-linear',
                        label: 'editorPage.createRoom',
                        tooltip: 'editorPage.createRoom',
                        onClick: () => setSelectedForRoomCreation && setSelectedForRoomCreation(presentation),
                    };
                case BUTTON_OPTIONS.SHARE_PRESENTATION:
                    return {
                        type: BUTTON_OPTIONS.SHARE_PRESENTATION,
                        icon: 'solar:share-linear',
                        label: 'editorPage.share',
                        tooltip: 'editorPage.share',
                        onClick: () => {
                            setSelected && setSelected([presentation]);
                            setSendPresentationModal && setSendPresentationModal(true);
                        },
                        disabled: presentation.isReadOnly,
                        dataCy: 'share_presentation',
                    };
                default:
                    return {
                        type: buttonType,
                        icon: '',
                        label: '',
                        tooltip: '',
                    };
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            dispatch,
            duplicatingPres,
            exportingPresentations,
            navigate,
            onCreatePresentationLinks,
            onMovePresentation,
            presentation,
            queryClient,
            setDeletePresentations,
            setPresentationToDelete,
            t,
            updateSharedPresentation,
        ]
    );

    const splitButtonOptions = (buttonOptions: BUTTON_OPTIONS[]): BUTTON_OPTIONS[][] => {
        const result: BUTTON_OPTIONS[][] = [];
        let currentArray: BUTTON_OPTIONS[] = [];

        buttonOptions.forEach((option) => {
            if (option === BUTTON_OPTIONS.DIVIDER) {
                if (currentArray.length > 0) {
                    result.push(currentArray);
                    currentArray = [];
                }
            } else {
                currentArray.push(option);
            }
        });

        if (currentArray.length > 0) {
            result.push(currentArray);
        }

        return result;
    };

    const splitOptions = useMemo(() => splitButtonOptions(buttonOptions), [buttonOptions]);
    const selectedButtons = useMemo(() => splitOptions.map((group) => group.map((option) => getButton(option))), [getButton, splitOptions]);
    const dropdownItems = useMemo(
        () =>
            selectedButtons.map((buttons) =>
                buttons.map(
                    (button) =>
                        ({
                            icon: button.icon,
                            label: t(button.label as TypedTranslation),
                            onClick: button.onClick,
                            disabled: button.disabled,
                            dataCy: button.dataCy,
                            color: button.color,
                        } as DropdownMenuCompactTileWithOnClick)
                )
            ),
        [selectedButtons, t]
    );

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
            }}
            className="u-flex u-v-center u-h-center"
        >
            {selectedButtons.length > 0 && (
                <DropdownMenuCompact
                    className="e-table__icon e-table__actions__icon"
                    items={dropdownItems}
                    menuPosition={DropdownMenuCompactMenuPosition.LEFT}
                >
                    <Button
                        leading="ph:dots-three-vertical-bold"
                        color="frost"
                        variant="ghost"
                        square
                        size="sm"
                        data-cy="moreActions"
                        type="button"
                    />
                </DropdownMenuCompact>
            )}
        </div>
    );
}
